import { Component, type ReactNode, useState } from 'react'
import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

interface Props {
  children: ReactNode
  fallbackLabel?: string
  locale?: LocaleCode
}

interface State {
  hasError: boolean
  error: Error | null
}

type ErrorCategory = 'chunk' | 'network' | 'api' | 'render'

interface CategorizedError {
  category: ErrorCategory
  title: string
  description: string
  actionLabel: string
  action: 'reload' | 'retry'
}

interface ErrorBoundaryCopy {
  chunkTitle: string
  chunkDescription: string
  networkTitle: string
  networkDescription: string
  apiTitle: string
  apiDescription: string
  renderTitle: (fallbackLabel?: string) => string
  renderDescription: string
  reload: string
  retry: string
  showDetails: string
  hideDetails: string
}

const ERROR_BOUNDARY_COPY: Record<LocaleCode, ErrorBoundaryCopy> = {
  en: {
    chunkTitle: 'App updated',
    chunkDescription: 'A new version has been deployed. Reload to get the latest version.',
    networkTitle: 'Connection lost',
    networkDescription: 'Check your internet connection and try again.',
    apiTitle: 'Server error',
    apiDescription: 'The server encountered an error. Please try again in a moment.',
    renderTitle: (fallbackLabel) => `This section encountered an error${fallbackLabel ? ` in ${fallbackLabel}` : ''}`,
    renderDescription: 'An unexpected error occurred. Please try again.',
    reload: 'Reload Page',
    retry: 'Retry',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
  },
  ko: {
    chunkTitle: '앱이 업데이트되었습니다',
    chunkDescription: '새 버전이 배포되었습니다. 최신 버전을 사용하려면 페이지를 새로고침하세요.',
    networkTitle: '연결이 끊어졌습니다',
    networkDescription: '인터넷 연결을 확인한 뒤 다시 시도하세요.',
    apiTitle: '서버 오류가 발생했습니다',
    apiDescription: '서버에서 오류가 발생했습니다. 잠시 후 다시 시도하세요.',
    renderTitle: () => '이 섹션에서 오류가 발생했습니다',
    renderDescription: '예기치 않은 오류가 발생했습니다. 다시 시도하세요.',
    reload: '페이지 새로고침',
    retry: '다시 시도',
    showDetails: '세부 정보 보기',
    hideDetails: '세부 정보 숨기기',
  },
  ja: {
    chunkTitle: 'アプリが更新されました',
    chunkDescription: '新しいバージョンが公開されました。再読み込みして最新版をご利用ください。',
    networkTitle: '接続が切れました',
    networkDescription: 'インターネット接続を確認して、もう一度お試しください。',
    apiTitle: 'サーバーエラーが発生しました',
    apiDescription: 'サーバーでエラーが発生しました。しばらくしてからもう一度お試しください。',
    renderTitle: () => 'このセクションでエラーが発生しました',
    renderDescription: '予期しないエラーが発生しました。もう一度お試しください。',
    reload: 'ページを再読み込み',
    retry: '再試行',
    showDetails: '詳細を表示',
    hideDetails: '詳細を非表示',
  },
  zh: {
    chunkTitle: '应用已更新',
    chunkDescription: '新版本已发布。请重新加载页面以使用最新版本。',
    networkTitle: '连接已断开',
    networkDescription: '请检查网络连接，然后重试。',
    apiTitle: '服务器出错',
    apiDescription: '服务器遇到错误，请稍后重试。',
    renderTitle: () => '此区域遇到错误',
    renderDescription: '发生了意外错误，请重试。',
    reload: '重新加载页面',
    retry: '重试',
    showDetails: '显示详细信息',
    hideDetails: '隐藏详细信息',
  },
  'zh-TW': {
    chunkTitle: '應用已更新',
    chunkDescription: '新版本已發佈。請重新載入頁面以使用最新版本。',
    networkTitle: '連線已中斷',
    networkDescription: '請檢查網路連線，然後再試一次。',
    apiTitle: '伺服器發生錯誤',
    apiDescription: '伺服器發生錯誤，請稍後再試一次。',
    renderTitle: () => '此區域發生錯誤',
    renderDescription: '發生未預期的錯誤，請再試一次。',
    reload: '重新載入頁面',
    retry: '再試一次',
    showDetails: '顯示詳細資訊',
    hideDetails: '隱藏詳細資訊',
  },
}

/** Categorize an error into a user-friendly type with specific messaging */
function categorizeError(error: Error, locale: LocaleCode, fallbackLabel?: string): CategorizedError {
  const msg = error.message ?? ''
  const name = error.name ?? ''
  const copy = ERROR_BOUNDARY_COPY[locale]

  // Chunk load errors (stale deployment)
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk')
  ) {
    return {
      category: 'chunk',
      title: copy.chunkTitle,
      description: copy.chunkDescription,
      actionLabel: copy.reload,
      action: 'reload',
    }
  }

  // Network errors
  if (
    name === 'TypeError' && (msg.includes('Failed to fetch') || msg.includes('NetworkError')) ||
    msg.includes('Network request failed') ||
    msg.includes('net::ERR_') ||
    msg.includes('ECONNREFUSED') ||
    msg.includes('timeout')
  ) {
    return {
      category: 'network',
      title: copy.networkTitle,
      description: copy.networkDescription,
      actionLabel: copy.retry,
      action: 'retry',
    }
  }

  // API / server errors
  if (
    msg.includes('500') || msg.includes('502') || msg.includes('503') ||
    msg.includes('Internal Server Error') ||
    msg.includes('Service Unavailable') ||
    msg.includes('Bad Gateway')
  ) {
    return {
      category: 'api',
      title: copy.apiTitle,
      description: copy.apiDescription,
      actionLabel: copy.retry,
      action: 'retry',
    }
  }

  // Render errors (default for React ErrorBoundary catches)
  return {
    category: 'render',
    title: copy.renderTitle(fallbackLabel),
    description: copy.renderDescription,
    actionLabel: copy.retry,
    action: 'retry',
  }
}

/** Collapsible details section for error debugging */
function ErrorDetails({ error, copy }: { error: Error; copy: ErrorBoundaryCopy }) {
  const [open, setOpen] = useState(false)
  if (!error.message && !error.stack) return null

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className="mt-2 w-full max-w-md text-left"
    >
      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none">
        {open ? copy.hideDetails : copy.showDetails}
      </summary>
      <pre className="mt-1 p-2 rounded-lg bg-muted text-[10px] text-muted-foreground overflow-auto max-h-32 whitespace-pre-wrap break-all">
        {error.message}
        {error.stack ? `\n\n${error.stack}` : ''}
      </pre>
    </details>
  )
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
    // Auto-reload on chunk load failures (stale deployment cache)
    const categorized = categorizeError(error, this.props.locale ?? getLocale())
    if (categorized.category === 'chunk') {
      const reloadKey = 'cogo_chunk_reload'
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1')
        window.location.reload()
      }
    }
  }

  handleRetry = () => {
    sessionStorage.removeItem('cogo_chunk_reload')
    this.setState({ hasError: false, error: null })
  }

  handleHardReload = () => {
    sessionStorage.removeItem('cogo_chunk_reload')
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      const error = this.state.error!
      const locale = this.props.locale ?? getLocale()
      const copy = ERROR_BOUNDARY_COPY[locale]
      const info = categorizeError(error, locale, this.props.fallbackLabel)
      const handleAction = info.action === 'reload' ? this.handleHardReload : this.handleRetry

      return (
        <div
          aria-live="assertive"
          className="h-full flex flex-col items-center justify-center gap-3 text-center p-6"
          role="alert"
        >
          <p className="text-sm font-medium text-foreground">{info.title}</p>
          <p className="text-xs text-muted-foreground max-w-md">
            {info.description}
          </p>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={handleAction}
              className="px-4 py-1.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
            >
              {info.actionLabel}
            </button>
          </div>
          {info.category !== 'chunk' && <ErrorDetails copy={copy} error={error} />}
        </div>
      )
    }
    return this.props.children
  }
}
